/**
 * Roving tabindex: many focusable children, one tab stop.
 *
 * **Why the tensor list needed this.** Every row is a real `<button>`, which is
 * right — a row is activatable and must announce as such — but it also made
 * every row a tab stop. Measured on `openai-community/gpt2`: 160 rows out of
 * 249 tab stops on the page, and the skip link targets `#tensor-view`, which
 * comes *after* the list in the DOM. So there was no way past the list at all,
 * which plan §14.3 requires there to be. The remedy is the standard one: the
 * list becomes a single tab stop and the arrow keys move within it.
 *
 * **Arrow keys move focus, never selection.** This is the rule that makes the
 * pattern safe here rather than merely conventional. Selecting a tensor issues
 * an HTTP Range read against the Hub; a list that selected as you arrowed
 * through it would fetch a tensor per keystroke, which is exactly the
 * prefetching this project promises not to do. Enter and Space activate, as
 * they already do on a button, and that is the only thing that reads bytes.
 *
 * Kept generic and DOM-only — no knowledge of tensors — because the anatomy
 * map needs the same bookkeeping for a two-dimensional grid and a second copy
 * of "set one to 0 and the rest to −1" is a second place for them to disagree.
 */

/** How long a typeahead buffer survives without another keystroke. */
const TYPEAHEAD_WINDOW_MS = 700;

/** Rows a Page Up / Page Down jumps. Arbitrary, and the same as a listbox's. */
const PAGE_STEP = 10;

export interface RovingFocusOptions {
  /** Matched against descendants of the container to find the items. */
  readonly itemSelector: string;
  /**
   * Text a typeahead keystroke matches against, or omitted to disable
   * typeahead. Matching is case-insensitive and by prefix.
   */
  readonly textOf?: (item: HTMLElement) => string;
}

/**
 * Give exactly one item `tabindex="0"` and every other item `tabindex="-1"`.
 *
 * Idempotent and safe to call on a freshly re-rendered list: the caller decides
 * which item is the entry point (the selected row, or the first one), and this
 * never moves focus — a list that grabbed focus when it re-rendered would steal
 * it from the filter box on every keystroke.
 */
export function setRovingTabindex(
  items: readonly Element[],
  activeIndex: number,
): void {
  if (items.length === 0) return;
  const active = Math.min(Math.max(activeIndex, 0), items.length - 1);
  items.forEach((item, index) => {
    item.setAttribute("tabindex", index === active ? "0" : "-1");
  });
}

/**
 * Wire arrow-key navigation to a container, once.
 *
 * Attach to the *container*, not to the items: the tensor list is re-rendered
 * wholesale on every filter keystroke and on every selection, so a per-item
 * listener would be re-bound hundreds of times a second and would leak the
 * previous generation's closures. The container outlives its children, so one
 * delegated listener covers every generation of them.
 */
export function attachRovingFocus(
  container: Element,
  options: RovingFocusOptions,
): void {
  let buffer = "";
  let bufferedAt = 0;

  const items = (): HTMLElement[] => [
    ...container.querySelectorAll<HTMLElement>(options.itemSelector),
  ];

  const itemFrom = (target: EventTarget | null): HTMLElement | null =>
    target instanceof Element
      ? target.closest<HTMLElement>(options.itemSelector)
      : null;

  const move = (list: readonly HTMLElement[], index: number): void => {
    const next = list[index];
    if (!next) return;
    setRovingTabindex(list, index);
    next.focus();
  };

  // Clicking a row makes it the entry point too, so a mouse user who then
  // reaches for the keyboard resumes where they were looking rather than at the
  // top of a 160-row list.
  container.addEventListener("focusin", (event) => {
    const item = itemFrom(event.target);
    if (!item) return;
    const list = items();
    const index = list.indexOf(item);
    if (index >= 0) setRovingTabindex(list, index);
  });

  container.addEventListener("keydown", (event) => {
    if (!(event instanceof KeyboardEvent)) return;
    const item = itemFrom(event.target);
    if (!item) return;
    const list = items();
    const current = list.indexOf(item);
    if (current < 0) return;
    const last = list.length - 1;

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        move(list, Math.min(current + 1, last));
        return;
      case "ArrowUp":
        event.preventDefault();
        move(list, Math.max(current - 1, 0));
        return;
      case "PageDown":
        event.preventDefault();
        move(list, Math.min(current + PAGE_STEP, last));
        return;
      case "PageUp":
        event.preventDefault();
        move(list, Math.max(current - PAGE_STEP, 0));
        return;
      case "Home":
        event.preventDefault();
        move(list, 0);
        return;
      case "End":
        event.preventDefault();
        move(list, last);
        return;
      default:
        break;
    }

    const textOf = options.textOf;
    if (!textOf) return;
    // Space activates the button underneath, and a modified key is a browser
    // shortcut. Neither is a search.
    if (event.key.length !== 1 || event.key === " ") return;
    if (event.altKey || event.ctrlKey || event.metaKey) return;

    const now = Date.now();
    const key = event.key.toLowerCase();
    buffer = now - bufferedAt > TYPEAHEAD_WINDOW_MS ? key : buffer + key;
    bufferedAt = now;

    // A repeated single character steps through the matches, which is how a
    // long list of `h.0.…`, `h.1.…` names is actually walked; a longer buffer
    // re-matches from where you are so extending it never jumps backwards.
    const start = buffer.length === 1 ? current + 1 : current;
    for (let step = 0; step < list.length; step += 1) {
      const index = (start + step) % list.length;
      const candidate = list[index];
      if (candidate && textOf(candidate).toLowerCase().startsWith(buffer)) {
        event.preventDefault();
        move(list, index);
        return;
      }
    }
  });
}
